/**
 * ClusterDetailModal - Detailed view of a keyword cluster
 *
 * Shows all keywords in a cluster with their individual metrics,
 * SERP features, article suggestions, and action buttons.
 */

import React from 'react';
import {
  Modal,
  Table,
  Tag,
  Space,
  Typography,
  Button,
  Tooltip,
  Descriptions,
  Divider,
  Empty,
  Progress,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Sparkles,
  ExternalLink,
  Target,
  TrendingUp,
  DollarSign,
  BarChart3,
  Copy,
  Check,
  Lightbulb,
  Globe,
} from 'lucide-react';
import { useCluster } from '@/hooks/useContentPlannerAPI';
import {
  KeywordData,
  ClusterSummary,
  ClusterDetail,
  SearchIntent,
  INTENT_LABELS,
  INTENT_COLORS,
  INTENT_FULL_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  getDifficultyLevel,
  DIFFICULTY_LABELS,
  DIFFICULTY_COLORS,
} from '@/types/contentPlanner';
import { toast } from 'sonner';
import loadingAnimation from '@/assets/loading-animation.webp';

const { Title, Text, Paragraph } = Typography;

interface ClusterDetailModalProps {
  tenantId: string | null;
  mapId: string | null;
  clusterId: string | null;
  onClose: () => void;
  onCreateArticle: (cluster: ClusterSummary) => void;
}

const ClusterDetailModal: React.FC<ClusterDetailModalProps> = ({
  tenantId,
  mapId,
  clusterId,
  onClose,
  onCreateArticle,
}) => {
  const { data: cluster, isLoading } = useCluster(tenantId, mapId, clusterId);
  const [copiedKeywords, setCopiedKeywords] = React.useState(false);

  const handleCopyKeywords = () => {
    if (!cluster) return;

    const keywords = cluster.keywords.map((k) => k.keyword).join('\n');
    navigator.clipboard.writeText(keywords);
    setCopiedKeywords(true);
    toast.success('Keywords copied to clipboard');
    setTimeout(() => setCopiedKeywords(false), 2000);
  };

  const columns: ColumnsType<KeywordData> = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text: string, record) => (
        <Space>
          {record.is_primary && (
            <Tooltip title="Primary Keyword">
              <Tag color="gold">Primary</Tag>
            </Tooltip>
          )}
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Intent',
      dataIndex: 'intent',
      key: 'intent',
      width: 80,
      render: (intent: SearchIntent) => (
        <Tooltip title={INTENT_FULL_LABELS[intent]}>
          <Tag color={INTENT_COLORS[intent]}>{INTENT_LABELS[intent]}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'MSV',
      dataIndex: 'monthly_search_volume',
      key: 'msv',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.monthly_search_volume - b.monthly_search_volume,
      defaultSortOrder: 'descend',
      render: (volume: number) => (
        <Text strong>{volume.toLocaleString()}</Text>
      ),
    },
    {
      title: 'KD',
      dataIndex: 'keyword_difficulty',
      key: 'kd',
      width: 80,
      sorter: (a, b) => a.keyword_difficulty - b.keyword_difficulty,
      render: (kd: number) => {
        const level = getDifficultyLevel(kd);
        return (
          <Tooltip title={DIFFICULTY_LABELS[level]}>
            <Tag color={DIFFICULTY_COLORS[level]}>{Math.round(kd)}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'CPC',
      dataIndex: 'cpc',
      key: 'cpc',
      width: 80,
      align: 'right',
      render: (cpc: number) => <Text type="secondary">${cpc?.toFixed(2) || '0.00'}</Text>,
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      key: 'trend',
      width: 100,
      render: (trend: string) => {
        if (!trend) return <Text type="secondary">-</Text>;
        // trend is typically a comma-separated list of monthly volumes
        const values = trend.split(',').map(Number).filter(Boolean);
        if (values.length === 0) return <Text type="secondary">-</Text>;

        const latest = values[values.length - 1];
        const oldest = values[0];
        const change = oldest > 0 ? ((latest - oldest) / oldest) * 100 : 0;

        return (
          <Tooltip title={`${change > 0 ? '+' : ''}${change.toFixed(0)}% change`}>
            <Tag color={change > 10 ? 'green' : change < -10 ? 'red' : 'default'}>
              {change > 0 ? '↑' : change < 0 ? '↓' : '→'}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Modal
      title={null}
      open={!!clusterId}
      onCancel={onClose}
      width={900}
      footer={null}
      styles={{ body: { padding: 0 } }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-96">
          <img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
        </div>
      ) : !cluster ? (
        <Empty description="Cluster not found" />
      ) : (
        <div>
          {/* Header */}
          <div className="p-6 border-b bg-muted">
            <div className="flex justify-between items-start">
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {cluster.name}
                </Title>
                <Space className="mt-2">
                  <Tag color={INTENT_COLORS[cluster.primary_intent]}>
                    {INTENT_FULL_LABELS[cluster.primary_intent]}
                  </Tag>
                  <Tag color={STATUS_COLORS[cluster.status]}>
                    {STATUS_LABELS[cluster.status]}
                  </Tag>
                  <Text type="secondary">{cluster.keyword_count} keywords</Text>
                </Space>
              </div>
              <Space>
                <Button icon={copiedKeywords ? <Check size={14} /> : <Copy size={14} />} onClick={handleCopyKeywords}>
                  {copiedKeywords ? 'Copied!' : 'Copy Keywords'}
                </Button>
                <Button
                  type="primary"
                  icon={<Sparkles size={14} />}
                  onClick={() => {
                    onCreateArticle(cluster);
                    onClose();
                  }}
                >
                  Create Article
                </Button>
              </Space>
            </div>
          </div>

          {/* Stats Row */}
          <div className="px-6 py-4 border-b">
            <Row gutter={24}>
              <Col span={6}>
                <Statistic
                  title="Total Volume"
                  value={cluster.total_volume}
                  prefix={<TrendingUp size={14} className="text-green-500 dark:text-green-400" />}
                  formatter={(value) => value?.toLocaleString()}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Avg. Difficulty"
                  value={Math.round(cluster.avg_difficulty)}
                  prefix={<BarChart3 size={14} className="text-orange-500 dark:text-orange-400" />}
                  suffix={
                    <Tag color={DIFFICULTY_COLORS[getDifficultyLevel(cluster.avg_difficulty)]} style={{ marginLeft: 8 }}>
                      {DIFFICULTY_LABELS[getDifficultyLevel(cluster.avg_difficulty)]}
                    </Tag>
                  }
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Avg. CPC"
                  value={cluster.avg_cpc?.toFixed(2) || '0.00'}
                  prefix={<DollarSign size={14} className="text-blue-500 dark:text-blue-400" />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Potential Traffic"
                  value={cluster.potential_traffic}
                  prefix={<Target size={14} className="text-teal" />}
                  formatter={(value) => value?.toLocaleString()}
                />
              </Col>
            </Row>
          </div>

          {/* Keywords Table */}
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Title level={5} style={{ margin: 0 }}>
                Keywords
              </Title>
            </div>
            <Table
              columns={columns}
              dataSource={cluster.keywords}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 10,
                hideOnSinglePage: true,
                showTotal: (total) => `${total} keywords`,
              }}
              scroll={{ y: 300 }}
            />
          </div>

          {/* Article Suggestions */}
          {cluster.article_suggestions && cluster.article_suggestions.length > 0 && (
            <div className="px-6 pb-6">
              <Divider style={{ margin: '0 0 16px 0' }} />
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={16} className="text-yellow-500 dark:text-yellow-400" />
                <Title level={5} style={{ margin: 0 }}>
                  Article Title Suggestions
                </Title>
              </div>
              <div className="space-y-2">
                {cluster.article_suggestions.map((suggestion, index) => (
                  <Card key={index} size="small" className="bg-muted">
                    <div className="flex justify-between items-center">
                      <Text>{suggestion}</Text>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => {
                          navigator.clipboard.writeText(suggestion);
                          toast.success('Title copied');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* SERP URLs */}
          {cluster.serp_urls && cluster.serp_urls.length > 0 && (
            <div className="px-6 pb-6">
              <Divider style={{ margin: '0 0 16px 0' }} />
              <div className="flex items-center gap-2 mb-3">
                <Globe size={16} className="text-blue-500 dark:text-blue-400" />
                <Title level={5} style={{ margin: 0 }}>
                  Top Ranking URLs
                </Title>
              </div>
              <div className="space-y-2">
                {cluster.serp_urls.slice(0, 5).map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Tag>{index + 1}</Tag>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 dark:text-blue-400 hover:text-blue-600 truncate flex-1"
                    >
                      <Space>
                        <ExternalLink size={12} />
                        <span>{url}</span>
                      </Space>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SERP Features */}
          {cluster.serp_features && Object.keys(cluster.serp_features).length > 0 && (
            <div className="px-6 pb-6">
              <Divider style={{ margin: '0 0 16px 0' }} />
              <Title level={5} style={{ margin: '0 0 12px 0' }}>
                SERP Features Present
              </Title>
              <Space wrap>
                {Object.entries(cluster.serp_features)
                  .filter(([_, present]) => present)
                  .map(([feature]) => (
                    <Tag key={feature} color="blue">
                      {feature.replace(/_/g, ' ')}
                    </Tag>
                  ))}
              </Space>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ClusterDetailModal;
